import { ApolloProvider, from, split } from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { ApolloClient, createHttpLink } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';
import { ReactNode, useMemo } from 'react';
import { createErrorLink } from '../handlers/errorHander';
import { SSELink } from '../links/SSELink';

export interface GraphQLProviderProps {
	children: ReactNode;
	uri: string;
	token?: string;
	/**
	 * Identifier for the calling app — sent on every request as the
	 * `x-app-audience` header. The auth subgraph uses this to scope
	 * refresh-token cookies per app (e.g. `codezo_refresh_token_admin`)
	 * so multiple apps on the same hostname can keep independent sessions.
	 *
	 * Required for any app that needs to log in / refresh / log out. If
	 * omitted, those mutations will be rejected by the backend.
	 */
	audience?: string;
	onUnauthorized?: () => void;
}

/**
 * Shared GraphQL Provider that creates and configures Apollo Client
 *
 * Usage:
 * - Without auth: <GraphQLProvider>{children}</GraphQLProvider>
 * - With auth: <GraphQLProvider getAuthToken={() => token}>{children}</GraphQLProvider>
 * - With custom endpoint: <GraphQLProvider uri="http://localhost:5000/graphql">{children}</GraphQLProvider>
 * - With error handling: <GraphQLProvider onUnauthorized={() => logout()}>{children}</GraphQLProvider>
 */
export function GraphQLProvider({
	children,
	uri,
	token,
	audience,
	onUnauthorized,
}: GraphQLProviderProps) {
	// Memoize the Apollo Client to prevent recreation on every render
	const client = useMemo(() => {
		const httpLink = createHttpLink({
			uri,
			// Send/receive HttpOnly cookies cross-origin (e.g. refresh-token cookie).
			// Requires the backend CORS to allowlist the frontend origin and set
			// `Access-Control-Allow-Credentials: true`.
			credentials: 'include',
		});

		// Auth Link for JWT tokens
		const authLink = setContext((_, { headers }) => {
			return {
				headers: {
					...headers,
					...(token && { authorization: `Bearer ${token}` }),
					...(audience && { 'x-app-audience': audience }),
				},
			};
		});

		const errorLink = createErrorLink(onUnauthorized ?? (() => {}));

		// Create SSE link for subscriptions
		const sseLink = new SSELink({
			uri,
			headers: () => {
				const headers: Record<string, string> = {};
				if (token) {
					headers.authorization = `Bearer ${token}`;
				}
				if (audience) {
					headers['x-app-audience'] = audience;
				}
				return headers;
			},
		});

		// Split link - use SSE for subscriptions, HTTP for queries/mutations
		const splitLink = split(
			({ query }) => {
				const definition = getMainDefinition(query);
				return (
					definition.kind === 'OperationDefinition' &&
					definition.operation === 'subscription'
				);
			},
			sseLink,
			from([authLink, httpLink])
		);

		// Configure Apollo Client
		return new ApolloClient({
			link: from([errorLink, splitLink]),
			cache: new InMemoryCache(),
			defaultOptions: {
				watchQuery: {
					errorPolicy: 'all',
					notifyOnNetworkStatusChange: true,
				},
				query: {
					errorPolicy: 'all',
					notifyOnNetworkStatusChange: true,
				},
			},
		});
	}, [uri, token, audience, onUnauthorized]);

	return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
